import { inject, Injectable, Injector, runInInjectionContext, Type } from "@angular/core";
import { catchError, Observable, OperatorFunction, throwError } from "rxjs";
import { OfflineError } from "../../network/errors/network-errors";

import { OfflineWorker } from "../workers/offline-worker";
import { ApplicationContext } from "../../context/application-context.service";
import { OfflinePayload } from "../../../models/offline-payload.model";

@Injectable({ providedIn: "root" })
/**
 * Shared entry point for offline-aware request handling across the app.
 * It applies the offline fallback policy and exposes a singleton for worker access.
 */
export class OfflineManagerService {
  private static instance: OfflineManagerService | null = null;
  private readonly injector = inject(Injector);
  private readonly appContext = inject(ApplicationContext);

  constructor() {
    OfflineManagerService.instance = this;
  }

  /**
   * Returns the singleton instance of the OfflineManagerService.
   */
  static getInstance(): OfflineManagerService {
    if (!OfflineManagerService.instance) {
      throw new Error("OfflineManagerService is not initialized");
    }
    return OfflineManagerService.instance;
  }

  /**
   * Returns an RxJS operator that applies the offline policy to an observable.
   *
   * @param {OfflinePayload<R> | R} payload - The request object or a wrapped payload.
   * @param {Type<OfflineWorker<T, R>>} [workerClass] - An optional worker class to handle offline logic.
   * @return {OperatorFunction<T, T>} An RxJS operator.
   */
  handleOfflineError<T, R = any>(
    payload: OfflinePayload<R> | R,
    workerClass: Type<OfflineWorker<T, R>> = OfflineWorker as Type<OfflineWorker<T, R>>,
    _groupKey?: string
  ): OperatorFunction<T, T> {
    const worker = runInInjectionContext(this.injector, () => inject(workerClass));
    return (source: Observable<T>) =>
      source.pipe(
        catchError(error => {
          if (error instanceof OfflineError) {
            return worker.handleOfflineError(payload, error);
          }
          return throwError(() => error);
        })
      );
  }
}
